/**
 * 图片上传组件
 *
 * 功能：
 * 1. 支持图片选择和预览
 * 2. 如果是加密图片（.bin结尾），自动解密后显示
 * 3. 上传时自动加密（AES-GCM）后直传S3
 * 4. 解密失败时显示错误占位符，支持重试
 *
 * 使用方式：
 * <Form.Item label="图片" name="image_url">
 *   <ImageUpload label="上传图片" />
 * </Form.Item>
 *
 * 状态管理说明：
 * - value: 外部传入的图片URL（由Form.Item控制）
 * - previewUrl: 解密后的预览URL（ObjectURL或原始URL）
 * - loading: 是否正在加载/解密
 * - error: 错误信息
 * - uploading: 是否正在上传
 */

import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Image, message, Space, Spin, Tooltip, Upload } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { growthConfigFacade } from '@/facades/growthConfig';
import {
  buildEncryptedFilename,
  decryptImageUrl,
  encryptFile,
  isEncryptedImageUrl,
} from '@/utils/imageCrypto';

interface ImageUploadProps {
  value?: string;
  onChange?: (url: string) => void;
  onUploaded?: (image: { url: string; width: number; height: number }) => void;
  onRemove?: () => void;
  label?: string;
  accept?: string;
  maxSize?: number;
  disabled?: boolean;
  category?: string;
  encrypt?: boolean;
  previewWidth?: number;
  previewHeight?: number;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  onUploaded,
  onRemove,
  label = '图片',
  accept = 'image/jpeg,image/png,image/webp,image/gif',
  maxSize = 10,
  disabled = false,
  category = 'comic',
  encrypt = true,
  previewWidth = 200,
  previewHeight = 200,
}) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isEncrypted, setIsEncrypted] = useState(false);
  const objectUrlRef = useRef<string>('');
  const localPreviewValueRef = useRef<string>('');
  const localPreviewUrlRef = useRef<string>('');
  const retryCountRef = useRef(0);
  const shouldTreatAsEncrypted = encrypt && ['tag-poster', 'comic-cover'].includes(category);

  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> =>
    new Promise((resolve) => {
      const objectUrl = URL.createObjectURL(file);
      const image = new window.Image();
      image.onload = () => {
        const dimensions = {
          width: image.naturalWidth || 0,
          height: image.naturalHeight || 0,
        };
        URL.revokeObjectURL(objectUrl);
        resolve(dimensions);
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({ width: 0, height: 0 });
      };
      image.src = objectUrl;
    });

  // 当value变化时，处理图片预览
  useEffect(() => {
    let isMounted = true;

    const loadPreview = async () => {
      if (!value) {
        setPreviewUrl('');
        setError('');
        setIsEncrypted(false);
        return;
      }

      const encrypted = isEncryptedImageUrl(value) || shouldTreatAsEncrypted;
      setIsEncrypted(encrypted);
      setLoading(true);
      setError('');

      try {
        if (localPreviewValueRef.current === value && localPreviewUrlRef.current) {
          setPreviewUrl(localPreviewUrlRef.current);
          retryCountRef.current = 0;
          return;
        }
        // 如果是加密图片，需要解密后显示
        if (encrypted) {
          console.log('[ImageUpload] 开始解密图片:', value);
          const decryptedUrl = await decryptImageUrl(value);
          if (isMounted) {
            // 清理旧的ObjectURL
            if (objectUrlRef.current) {
              URL.revokeObjectURL(objectUrlRef.current);
            }
            objectUrlRef.current = decryptedUrl;
            setPreviewUrl(decryptedUrl);
            console.log('[ImageUpload] 解密成功');
          }
        } else {
          // 普通图片直接显示
          setPreviewUrl(value);
        }
        // 成功后重置重试计数
        retryCountRef.current = 0;
      } catch (err: any) {
        console.error('[ImageUpload] 加载图片预览失败:', err);
        if (isMounted) {
          // 构建详细的错误信息
          let errorMsg = '图片加载失败';
          if (err?.message?.includes('获取密钥失败')) {
            errorMsg = '获取解密密钥失败';
          } else if (err?.message?.includes('下载加密图片失败')) {
            errorMsg = '下载图片失败';
          } else if (err?.message?.includes('加密图片数据无效')) {
            errorMsg = '图片数据无效';
          } else if (
            err?.name === 'OperationError' ||
            err?.message?.includes('decrypt')
          ) {
            errorMsg = '解密失败，密钥可能不匹配';
          } else if (err?.message) {
            errorMsg = err.message;
          }
          setError(errorMsg);
          setPreviewUrl('');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      isMounted = false;
      // 组件卸载时清理ObjectURL
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, [value, shouldTreatAsEncrypted]);

  useEffect(() => {
    return () => {
      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
      }
    };
  }, []);

  // 上传前的处理
  const beforeUpload = async (file: File) => {
    // 检查文件类型
    if (!accept.includes(file.type)) {
      message.error(`不支持的文件类型: ${file.type}`);
      return false;
    }

    // 检查文件大小
    if (file.size > maxSize * 1024 * 1024) {
      message.error(`文件大小不能超过 ${maxSize}MB`);
      return false;
    }

    return true;
  };

  // 自定义上传逻辑
  const customUpload = async (options: any) => {
    const { file, onSuccess, onError } = options;

    setUploading(true);
    try {
      // 1. 获取文件扩展名
      const ext = file.name.split('.').pop() || 'bin';
      const uploadBody = encrypt ? await encryptFile(file) : file;
      const fileHash = encrypt ? buildEncryptedFilename(file.name) : file.name;
      const contentType = encrypt ? 'application/octet-stream' : (file.type || 'application/octet-stream');

      // 2. 获取预签名URL
      const presignedResult = await growthConfigFacade.getUploadPresignedUrl({
        category,
        contentType,
        fileHash,
        ext,
      });

      // 3. 直传S3
      const uploadResponse = await fetch(presignedResult.url, {
        method: 'PUT',
        body: uploadBody,
        headers: {
          'Content-Type': contentType,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`上传失败: ${uploadResponse.status}`);
      }

      // 4. 返回最终URL
      const finalUrl = presignedResult.finalUrl;
      const dimensions = await getImageDimensions(file as File);
      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
      }
      localPreviewValueRef.current = finalUrl;
      localPreviewUrlRef.current = URL.createObjectURL(file as File);
      setPreviewUrl(localPreviewUrlRef.current);
      setError('');
      setIsEncrypted(encrypt);

      message.success('上传成功');
      onChange?.(finalUrl);
      onUploaded?.({ url: finalUrl, ...dimensions });
      onSuccess?.(finalUrl);
    } catch (err: any) {
      console.error('上传失败:', err);
      message.error(err?.message || '上传失败');
      onError?.(err);
    } finally {
      setUploading(false);
    }
  };

  // 重新加载图片
  const handleRetry = () => {
    if (retryCountRef.current >= 3) {
      message.warning('重试次数过多，请检查图片或刷新页面');
      return;
    }
    retryCountRef.current += 1;
    // 触发重新加载 - 通过临时清除再设置value
    const currentValue = value;
    setError('');
    setLoading(true);
    // 使用setTimeout触发useEffect重新执行
    setTimeout(() => {
      if (currentValue) {
        // 强制重新加载
        const loadPreview = async () => {
          try {
            if (isEncryptedImageUrl(currentValue) || shouldTreatAsEncrypted) {
              const decryptedUrl = await decryptImageUrl(currentValue);
              if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
              }
              objectUrlRef.current = decryptedUrl;
              setPreviewUrl(decryptedUrl);
            } else {
              setPreviewUrl(currentValue);
            }
            setError('');
            retryCountRef.current = 0;
          } catch (err: any) {
            let errorMsg = '图片加载失败';
            if (err?.message?.includes('获取密钥失败')) {
              errorMsg = '获取解密密钥失败';
            } else if (err?.message?.includes('下载加密图片失败')) {
              errorMsg = '下载图片失败';
            } else if (err?.name === 'OperationError') {
              errorMsg = '解密失败，密钥可能不匹配';
            } else if (err?.message) {
              errorMsg = err.message;
            }
            setError(errorMsg);
          } finally {
            setLoading(false);
          }
        };
        loadPreview();
      }
    }, 100);
  };

  // 删除图片
  const handleRemove = () => {
    onChange?.('');
    onRemove?.();
    setPreviewUrl('');
    setError('');
    setIsEncrypted(false);
    localPreviewValueRef.current = '';
    if (localPreviewUrlRef.current) {
      URL.revokeObjectURL(localPreviewUrlRef.current);
      localPreviewUrlRef.current = '';
    }
  };

  // 上传按钮
  const uploadButton = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
      }}
    >
      {uploading ? <Spin /> : <PlusOutlined style={{ fontSize: 24 }} />}
      <div style={{ fontSize: 14 }}>{uploading ? '上传中...' : label}</div>
    </div>
  );

  // 渲染错误占位符 - 解密失败时显示
  const renderErrorPlaceholder = () => (
    <div
      style={{
        width: previewWidth,
        height: previewHeight,
        border: '1px dashed #ff4d4f',
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        background: '#fff2f0',
        padding: 16,
      }}
    >
      <ExclamationCircleOutlined style={{ fontSize: 32, color: '#ff4d4f' }} />
      <div
        style={{
          color: '#ff4d4f',
          fontSize: 13,
          fontWeight: 500,
          textAlign: 'center',
        }}
      >
        图片加载失败
      </div>
      <Tooltip title={error}>
        <div
          style={{
            color: '#666',
            fontSize: 12,
            textAlign: 'center',
            maxWidth: 160,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {error}
        </div>
      </Tooltip>
      {isEncrypted && (
        <div style={{ color: '#999', fontSize: 11 }}>(加密图片)</div>
      )}
      <Space size={8}>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={handleRetry}
          type="primary"
          ghost
        >
          重试
        </Button>
        {!disabled && (
          <Button size="small" onClick={handleRemove}>
            移除
          </Button>
        )}
      </Space>
    </div>
  );

  // 渲染加载占位符
  const renderLoadingPlaceholder = () => (
    <div
      style={{
        width: previewWidth,
        height: previewHeight,
        border: '1px dashed #d9d9d9',
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        background: '#fafafa',
      }}
    >
      <Spin />
      <div style={{ color: '#666', fontSize: 12 }}>
        {isEncrypted ? '解密中...' : '加载中...'}
      </div>
    </div>
  );

  return (
    <div
      className="image-upload-wrapper"
      style={
        {
          '--image-upload-width': `${previewWidth}px`,
          '--image-upload-height': `${previewHeight}px`,
        } as React.CSSProperties
      }
    >
      <style>
        {`
          .image-upload-wrapper .avatar-uploader .ant-upload-select {
            width: var(--image-upload-width) !important;
            height: var(--image-upload-height) !important;
          }
        `}
      </style>
      {error ? (
        renderErrorPlaceholder()
      ) : loading ? (
        renderLoadingPlaceholder()
      ) : previewUrl ? (
        <div
          style={{
            position: 'relative',
            width: previewWidth,
            height: previewHeight,
            border: '1px solid #d9d9d9',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <Image
            src={previewUrl}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            placeholder={<Spin />}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
          />
          {!disabled && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                padding: 4,
                background: 'rgba(0,0,0,0.5)',
                borderRadius: '0 8px 0 8px',
                display: 'flex',
                gap: 8,
              }}
            >
              <DeleteOutlined
                style={{ color: '#fff', cursor: 'pointer', fontSize: 16 }}
                onClick={handleRemove}
              />
            </div>
          )}
        </div>
      ) : (
        <Upload
          name="file"
          listType="picture-card"
          className="avatar-uploader"
          showUploadList={false}
          accept={accept}
          disabled={disabled || uploading}
          beforeUpload={beforeUpload}
          customRequest={customUpload}
          style={{ width: previewWidth, height: previewHeight }}
        >
          {uploadButton}
        </Upload>
      )}
    </div>
  );
};

export default ImageUpload;
